import { PaymentMethod, UserRole } from '@avanzu/types';
import AddIcon from '@mui/icons-material/Add';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import AddPaymentMethodForm from '../../../components/AddPaymentMethodForm';
import PaperSection from '../../../components/PaperSection';
import { useAuth } from '../../../contexts/AuthContext';
import ContentWrapper from '../../../layout/ContentWrapper';
import { apiClient } from '../../../services/apiClient';
import { CreditCard } from '@mui/icons-material';
import { EmptyStateContent } from '../../../components/EmptyStateContent';

interface PaymentMethodsResponse {
  paymentMethods: PaymentMethod[];
  hasMore: boolean;
  totalCount: number;
  defaultPaymentMethodId: string | null;
  subscriptionDefaultMethodId: string | null;
}

export const CardRoles = [UserRole.AVANZU, UserRole.OWNER, UserRole.ADMIN];

const PaymentCards: React.FC = () => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [settingDefault, setSettingDefault] = useState<string | null>(null);
  const [deletingMethod, setDeletingMethod] = useState<string | null>(null);
  const { organisationId } = useAuth();

  const fetchPaymentMethods = async (isInitialLoad = false) => {
    if (!organisationId) {
      setError('Organisation ID is missing');
      return;
    }

    try {
      if (isInitialLoad) {
        setInitialLoading(true);
      }
      const response = await apiClient.get(
        `/organisations/${organisationId}/stripe/payment-methods/list`
      );

      if (!response.ok) {
        // Check if it's a 404 or empty result, which is not an error
        if (response.status === 404) {
          setPaymentMethods([]);
          return;
        }
        throw new Error('Failed to fetch card details');
      }
      const data: PaymentMethodsResponse = await response.json(); // Handle empty payment methods gracefully
      if (!data.paymentMethods || data.paymentMethods.length === 0) {
        setPaymentMethods([]);
      } else {
        setPaymentMethods(
          data.paymentMethods.sort((a, b) => {
            if (a.id < b.id) return -1;
            if (a.id > b.id) return 1;
            return 0;
          })
        );
      }
    } catch (err: any) {
      // Only show error for actual errors, not empty states
      if (err.message !== 'Failed to fetch card details' || !isInitialLoad) {
        setError(err.message);
      }
    } finally {
      if (isInitialLoad) {
        setInitialLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchPaymentMethods(true);
  }, []);

  const handleSetDefault = async (paymentMethodId: string) => {
    try {
      setSettingDefault(paymentMethodId);
      const response = await apiClient.post(
        `/organisations/${organisationId}/stripe/payment-methods/set-default`,
        {
          body: JSON.stringify({
            paymentMethodId,
            updateSubscriptions: true,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to set default card');
      }

      // Update payment methods locally instead of refetching
      setPaymentMethods((prev) =>
        prev.map((method) => ({
          ...method,
          isDefault: method.id === paymentMethodId,
        }))
      );
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSettingDefault(null);
    }
  };

  const handleDelete = async (paymentMethodId: string) => {
    try {
      setDeletingMethod(paymentMethodId);

      const response = await apiClient.delete(
        `/organisations/${organisationId}/stripe/payment-methods/delete/${paymentMethodId}`
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete card');
      }

      // Remove the payment method locally instead of refetching
      setPaymentMethods((prev) => prev.filter((method) => method.id !== paymentMethodId));
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeletingMethod(null);
    }
  };

  const formatCardBrand = (brand: string) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const handleAddSuccess = () => {
    setShowAddForm(false);
    fetchPaymentMethods(false);
  };

  if (initialLoading) {
    return (
      <ContentWrapper breadcrumb={true}>
        <PaperSection
          title="Credit Cards"
          action={
            <Button startIcon={<AddIcon />} variant="contained" disabled>
              Add Credit Card
            </Button>
          }
        >
          <Grid container spacing={3} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <CardSkeleton />
            </Grid>
          </Grid>
          <Box sx={{ pt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Note: Cards and payments are handled by Stripe, a secure and trusted payment
              processor. <br />
              We do not store your card details on our servers.
            </Typography>
          </Box>
        </PaperSection>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper breadcrumb={true}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <PaperSection
        title="Credit Cards"
        action={
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            disabled={showAddForm}
            onClick={() => setShowAddForm(true)}
          >
            Add Credit Card
          </Button>
        }
      >
        <Collapse in={showAddForm}>
          <AddPaymentMethodForm
            onSuccess={handleAddSuccess}
            onCancel={() => setShowAddForm(false)}
            isFirstCard={paymentMethods.length === 0} // Pass whether this is the first card
          />
        </Collapse>

        {paymentMethods.length === 0 ? (
          <Box textAlign="center" py={4}>
            <EmptyStateContent
              icon={<CreditCard fontSize="large" />}
              title="No payment cards found"
              message="You have no payment cards yet. Payment cards will appear here once available."
              showBorder={false}
            />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {paymentMethods.map((method) => (
              <Grid item xs={12} md={6} key={method.id}>
                <Box sx={{ position: 'relative' }}>
                  {method.isDefault && (
                    <Box
                      sx={{
                        position: 'absolute',
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        top: -12,
                      }}
                    >
                      <Chip label="Primary Card" size="small" color="primary" sx={{ zIndex: 1 }} />
                    </Box>
                  )}
                  <Card
                    variant="outlined"
                    sx={{
                      minHeight: '140px',
                      backgroundColor: '#fff',
                      ...(method.isDefault
                        ? {
                            borderColor: 'primary.main',
                            borderWidth: 2,
                            boxShadow: '0 0 0 1px rgba(25, 118, 210, 0.5)',
                          }
                        : {}),
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="start">
                        <Box>
                          <Stack direction="row" spacing={2} alignItems="center" mb={1}>
                            <CreditCardIcon color="primary" />
                            <Typography variant="body1" fontWeight="medium">
                              {method.card && `•••• •••• •••• ${method.card.last4}`}
                            </Typography>
                          </Stack>
                          {method.card && (
                            <>
                              <Typography variant="body2" color="text.secondary">
                                {formatCardBrand(method.card.brand)} - Expires{' '}
                                {method.card.expMonth}/{method.card.expYear}
                              </Typography>
                              {!method.isDefault && (
                                <Button
                                  size="small"
                                  sx={{ fontSize: '0.75rem', mt: 0.5 }}
                                  variant="text"
                                  onClick={() => handleSetDefault(method.id)}
                                  disabled={settingDefault === method.id}
                                >
                                  {settingDefault === method.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    'Set primary card'
                                  )}
                                </Button>
                              )}
                            </>
                          )}
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            size="small"
                            onClick={() => setDeleteConfirm(method.id)}
                            color="error"
                            title="Delete payment method"
                            disabled={deletingMethod === method.id}
                          >
                            {deletingMethod === method.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Stack>
                      </Box>

                      <Collapse in={deleteConfirm === method.id && deletingMethod !== method.id}>
                        <Box
                          sx={{
                            mt: 2,
                            pt: 2,
                            borderTop: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="body2" color="error" gutterBottom>
                            Are you sure you want to delete this payment method?
                          </Typography>
                          <Stack direction="row" spacing={1} mt={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => setDeleteConfirm(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => handleDelete(method.id)}
                            >
                              Delete
                            </Button>
                          </Stack>
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
        <Box sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary">
            Note: Cards and payments are handled by Stripe, a secure and trusted payment processor.
            <br />
            We do not store your card details on our servers.
          </Typography>
        </Box>
      </PaperSection>
    </ContentWrapper>
  );
};

export default PaymentCards;

const CardSkeleton: React.FC = () => (
  <Card
    variant="outlined"
    sx={{
      minHeight: '140px',
      backgroundColor: '#fff',
    }}
  >
    <CardContent sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="start">
        <Box sx={{ width: '100%' }}>
          <Stack direction="row" spacing={2} alignItems="center" mb={1}>
            <Skeleton variant="circular" width={24} height={24} />
            <Skeleton variant="text" width={160} height={24} />
          </Stack>
          <Skeleton variant="text" width={120} height={20} />
          <Skeleton variant="text" width={100} height={28} sx={{ mt: 1 }} />
        </Box>
        <Skeleton variant="circular" width={32} height={32} />
      </Box>
    </CardContent>
  </Card>
);
